import * as React from "react";
import QrReader from "react-qr-reader";
import { view } from "@orderofchaos/mvvm";
import Icon, { IconSize } from "@/components/Icon/Icon";
import styles from "./QrScannerOverlay.module.css";
import { QrScannerOverlayViewModel } from "./QrScannerOverlayViewModel";

export interface QrScannerOverlayProps {
  isOpen: boolean;
  title: string;
  description: string;
  delay: number | false;
  selectedPayload?: string;
  selectedLabel?: string;
  error?: string | null;
  idleText?: string;
  isResolving?: boolean;
  cameraPreview?: React.ReactNode;
  onClose: () => void;
  onScan: (value: string | null) => void | Promise<void>;
  onError: (error: unknown) => void;
}

export const QrScannerOverlay: React.FC<QrScannerOverlayProps> = view(
  QrScannerOverlayViewModel,
)<QrScannerOverlayProps>(({ viewModel }) => {
  if (!viewModel.isOpen) {
    return null;
  }

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby={viewModel.titleId}
      aria-describedby={viewModel.ariaDescriptionId}
    >
      <header className={styles.header}>
        <span className={styles.iconBadge} aria-hidden="true">
          <Icon name="QR" size={IconSize.medium} />
        </span>
        <div className={styles.headerText}>
          <h2 id={viewModel.titleId} className={styles.title}>
            {viewModel.title}
          </h2>
          <p id={viewModel.descriptionId} className={styles.description}>
            {viewModel.description}
          </p>
        </div>
        <button
          type="button"
          className={styles.closeButton}
          onClick={viewModel.onCloseClick}
          aria-label="Закрыть сканер QR"
          title="Закрыть сканер QR"
        >
          <Icon name="Close" size={IconSize.small} />
        </button>
      </header>

      <div className={styles.cameraArea}>
        {viewModel.cameraPreview ? (
          <div className={styles.readerPreview}>{viewModel.cameraPreview}</div>
        ) : (
          <QrReader
            delay={viewModel.delay}
            facingMode="environment"
            onError={viewModel.onQrScanError}
            onScan={viewModel.onQrScan}
            className={styles.reader}
            style={{ width: "100%" }}
          />
        )}
        <div className={styles.scanFrame} aria-hidden="true" />
      </div>

      <footer className={styles.footer}>
        <div
          id={viewModel.statusId}
          className={`${styles.status} ${styles[viewModel.statusTone]}`}
          role="status"
          aria-live="polite"
        >
          <span className={styles.statusDot} aria-hidden="true" />
          <span className={styles.statusText}>{viewModel.statusText}</span>
        </div>
      </footer>
    </div>
  );
});
